import { InputText } from '@teo/components';
import {
  useState,
  Fragment,
  forwardRef,
  InputHTMLAttributes,
  Ref,
  ChangeEvent,
  useRef,
} from 'react';
import {
  ChevronDownFilled,
  Search,
  IconComponent,
} from '@teo/components/icons';
import { Combobox, Portal } from '@headlessui/react';
import clsx from 'clsx';

type InputElementType = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'placeholder'
>;

export type InputTextProps = {
  Icon?: IconComponent;
  customStyles?: string;
  hasError?: boolean;
  iconPosition?: 'left' | 'right';
  innerRef?: Ref<HTMLInputElement>;
  inputClassName?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  textAlign?: 'left' | 'center';
} & InputElementType;

const defaultFilterFn =
  (query: string) =>
  ({ name }: any) =>
    name.toLowerCase().includes(query.toLowerCase());

// eslint-disable-next-line react/display-name
const CustomInput = forwardRef<HTMLInputElement, InputTextProps>(
  (props, ref: any) => <InputText Icon={Search} {...props} innerRef={ref} />
);

export const SelectSearchDropdown = ({
  dropdownProps,
  options,
  onChange,
  value,
  buttonText,
  children,
  dropdownClassName,
  usePortal = false,
}: any) => {
  const [query, setQuery] = useState<any>('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const filteredOptions =
    query === '' ? options : options.filter(defaultFilterFn(query));

  const dropdownContent = (
    <div
      className={
        dropdownClassName ||
        'mt-2 w-52 rounded-lg bg-white p-2 drop-shadow-s md:w-60 lg:w-72'
      }
    >
      <Combobox.Input
        onChange={(event) => setQuery(event.currentTarget.value)}
        value={query}
        as={CustomInput}
      />
      <Combobox.Options
        className={clsx(['mt-1 max-h-40 space-y-1 overflow-auto pr-1'])}
        {...dropdownProps}
      >
        {filteredOptions.map((option: any) => (
          <Combobox.Option as={Fragment} key={option.id} value={option}>
            {({ selected }) => (
              <div
                className={clsx([
                  'flex items-center gap-2 rounded px-3 py-2 text-sm',
                  'hover:cursor-pointer hover:bg-grey-01 active:bg-grey-02',
                  selected ? 'bg-secondary-01' : 'bg-transparent',
                ])}
              >
                {children(option)}
              </div>
            )}
          </Combobox.Option>
        ))}
      </Combobox.Options>
    </div>
  );

  return (
    <Combobox value={value} onChange={onChange}>
      {({ open }) => (
        <>
          <Combobox.Button
            ref={buttonRef}
            className={clsx(
              'flex h-[40px] w-full items-center gap-2 rounded-md bg-grey-01 py-2 px-3 text-base text-grey-08 hover:bg-grey-02',
              'outline-primary-02 focus:outline focus:outline-4'
            )}
          >
            <span className="flex items-center">
              {value && (
                <span
                  style={{ backgroundColor: value.color }}
                  className="mr-2 h-2 w-2 rounded-full"
                />
              )}
              {value ? `${value.name}` : buttonText}
            </span>

            <>
              {open ? (
                <ChevronDownFilled className="ml-auto h-5 w-5 " />
              ) : (
                <ChevronDownFilled className="ml-auto h-5 w-5 rotate-180" />
              )}
            </>
          </Combobox.Button>
          {open &&
            (usePortal && buttonRef.current ? (
              <Portal>
                <div
                  className="fixed z-[200]"
                  style={(() => {
                    const bounds = buttonRef.current.getBoundingClientRect();
                    const openUpward =
                      window.innerHeight - bounds.bottom < 260 &&
                      bounds.top > window.innerHeight - bounds.bottom;

                    return openUpward
                      ? {
                          bottom: `${window.innerHeight - bounds.top + 8}px`,
                          left: `${bounds.left}px`,
                          width: `${bounds.width}px`,
                        }
                      : {
                          top: `${bounds.bottom + 8}px`,
                          left: `${bounds.left}px`,
                          width: `${bounds.width}px`,
                        };
                  })()}
                >
                  {dropdownContent}
                </div>
              </Portal>
            ) : (
              <div className="relative">
                <div className="absolute z-50">{dropdownContent}</div>
              </div>
            ))}
        </>
      )}
    </Combobox>
  );
};
